import React, {useEffect, useState, useRef} from 'react';
import useAppContext from '../../contexts/UseAppContext';
import QRScanner from '../../components/QRScanner/QRScanner';
import NavigationBarV2 from '../../components/NavigationBarV2/NavigationBarV2';
import QRScannerMessage from '../../components/QRScannerMessage/QRScannerMessage';
import ProductValidationModal from './ProductValidationModal/ProductValidationModal';
import DataLoader from '../../components/DataLoader/DataLoader';
import DataError from '../../components/DataError/DataError';
import Activity from '../../helpers/Activity';
import Viewport from '../../helpers/Viewport';
import Debug from '../../helpers/Debug';
import {Html5Qrcode} from 'html5-qrcode';
import './Scanner.css';

function Scanner({section}) {
    const context = useAppContext();
    const timeoutRef = useRef(null);

    const showHeaderAndFooter = () => {
        return Viewport.dimensions.height <= 600;
    };

    const getScannerSize = () => {
        const newWidth = Viewport.dimensions.width;
        let newHeight;
        if (showHeaderAndFooter()) newHeight = Viewport.dimensions.height;
        else newHeight = Viewport.dimensions.height - context.navigationBarHeight - context.qrScannerMessageHeight + context.qrScannerOffsetHeight;
        return {width: newWidth, height: newHeight};
    };

    const [isLoaded, setIsLoaded] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [error, setError] = useState(undefined);
    const [finalQRScannerSize, setFinalQRScannerSize] = useState(getScannerSize());
    const [scanResult, setScanResult] = useState(undefined);
    const [cameras, setCameras] = useState([]);

    useEffect(() => {
        setResizeListener();
        requestPermissions();
        return () => {
            removeResizeListener();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setResizeListener = () => {
        window.addEventListener('resize', handleResize);
    };

    const removeResizeListener = () => {
        window.removeEventListener('resize', handleResize);
    };

    const handleResize = () => {
        startResizeTimeout();
    };

    const startResizeTimeout = () => {
        stopResizeTimeout();
        setIsResizing(true);
        timeoutRef.current = setTimeout(() => {
            setFinalQRScannerSize(getScannerSize());
            setIsResizing(false);
        }, 2000);
    };

    const stopResizeTimeout = () => {
        if (timeoutRef && timeoutRef.current) clearTimeout(timeoutRef.current);
    };

    const requestPermissions = () => {
        Html5Qrcode.getCameras()
            .then((cameras) => {
                if (!cameras || cameras.length === 0) {
                    Debug.printToLog('info', '[Scanner] No cameras found');
                    throw new Error('NoCamerasFound');
                }
                setCameras(cameras);
                Debug.printToLog('info', `[Scanner] Found ${cameras.length} camera(s)`);
                cameras.forEach((camera, index) => {
                    Debug.printToLog('info', `[Scanner] [${index + 1}] Label: '${camera.label}' | ID: '${camera.id}'`);
                });
                onQRScannerInit();
            })
            .catch((_error) => {
                onQRScannerError('initialize', _error);
            });
    };

    const onQRScannerInit = () => {
        setError(undefined);
        setIsLoaded(true);
        Activity.log('integrated-scanner', 'qr-scanner', 'initialize', {status: 'success', error: null});
    };

    const onQRScannerError = (action, _error) => {
        const errorMessage = _error ? _error.toString() : 'UnknownError';
        Debug.printToLog('error', `QRScanner Initialization error | ${_error}`);
        let type;
        if (errorMessage.includes('NotAllowedError')) type = 'NotAllowedError';
        else if (errorMessage.includes('NotSupportedError')) type = 'NotSupportedError';
        else if (errorMessage.includes('NoCamerasFound')) type = 'NoCamerasFound';
        else type = 'UnknownError';
        setError(type);
        setIsLoaded(true);
        Activity.log('integrated-scanner', 'qr-scanner', action, {status: 'failed', error: type});
    };

    const onQRScannerScan = (result) => {
        if (!scanResult) {
            Debug.printToLog('info', `[Scanner] result: ${result}`);
            Activity.log('integrated-scanner', 'qr-scanner', 'scan', {status: 'success', scan_result: result});
            setScanResult(result);
        }
    };

    const closeProductValidationModal = () => {
        setScanResult(undefined);
    };

    const renderLoader = () => {
        return (
            <div className='scanner_loader'>
                <DataLoader message='Esperando permiso para acceder a la cámara...' loaderSize={80}/>
            </div>
        );
    };

    const renderResizer = () => {
        return (
            <div className='scanner_loader'>
                <DataLoader message='Recalculando dimensiones de la cámara...' loaderSize={80}/>
            </div>
        );
    };

    const renderError = () => {
        let message;
        switch (error) {
            case 'NoCamerasFound':
                message = 'No se han encontrado cámaras accesibles en este dispositivo.';
                break;
            case 'NotAllowedError':
                message = 'Se ha rechazado el permiso para acceder a la cámara.';
                break;
            case 'NotSupportedError':
                message = 'Este dispositivo no soporta la lectura de códigos QR o el uso de la cámara.';
                break;
            default:
                message = 'Se ha producido un error al iniciar el escáner.';
        }
        return (
            <div className='scanner_error'>
                <DataError message={message} actionLabel={undefined} action={undefined} padding={undefined} maxWidth={undefined} iconSize={100} fontSize={15}/>
            </div>
        );
    };

    const renderValidationModal = () => {
        return <ProductValidationModal scanResult={scanResult} onClose={closeProductValidationModal}/>
    };

    return (
        <div className='scanner'>
            {!showHeaderAndFooter() && (
                <div className='scanner_navigation-bar'>
                    <NavigationBarV2 section={section} showCartLink={true}/>
                </div>
            )}
            {!isLoaded && renderLoader()}
            {isLoaded && error && renderError()}
            {isLoaded && !error && isResizing && renderResizer()}
            {isLoaded && !error && !isResizing && (
                <div className='scanner_qr-scanner'>
                    <QRScanner
                        width={finalQRScannerSize.width}
                        height={finalQRScannerSize.height}
                        cameras={cameras}
                        onScan={onQRScannerScan}
                        onError={onQRScannerError}
                    />
                </div>
            )}
            {!showHeaderAndFooter() && (
                <div className='scanner_qr-scanner-message'>
                    <QRScannerMessage/>
                </div>
            )}
            {scanResult && renderValidationModal()}
        </div>
    );
}

export default Scanner;